import React, { useEffect, useState } from 'react';
import ChatFileSelector from 'components/DisasterGPT/ChatFileSelector';
import {
  useAttachedFiles,
  useAttachFiles,
  useDetachFile,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import Loader from 'components/Loader/Loader';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { toast } from 'react-toastify';

interface ReportsFileAttachmentsProps {
  sitrepId: string;
  workspaceId: string | null; // incident_id from sitrep
  readOnly?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const ReportsFileAttachments: React.FC<ReportsFileAttachmentsProps> = ({
  sitrepId,
  workspaceId,
  readOnly = false,
  isExpanded = false,
  onExpandedChange = () => {},
}) => {
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  // Query hooks
  const { data: attachedFiles = [], isLoading: loadingAttached } =
    useAttachedFiles(sitrepId);

  // Mutation hooks
  const attachMutation = useAttachFiles();
  const detachMutation = useDetachFile();
  const { mutateAsync: getSignedUrl } = useSignedUrl();

  useEffect(() => {
    if (isExpanded) {
      setSelectedFileIds(attachedFiles.map((f) => f.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const handleAddFiles = async () => {
    if (selectedFileIds.length === 0) return;

    // Filter out files that are already attached to avoid duplicates
    const alreadyAttachedIds = attachedFiles.map((f) => f.id);
    const newFileIds = selectedFileIds.filter(
      (id) => !alreadyAttachedIds.includes(id)
    );

    // Only attach if there are new files
    if (newFileIds.length === 0) {
      onExpandedChange(false);
      return;
    }

    await attachMutation.mutateAsync({
      sitrepId,
      fileIds: newFileIds,
    });

    // Reset selection and collapse
    setSelectedFileIds([]);
    onExpandedChange(false);
  };

  const handleDetachFile = async (fileId: string) => {
    await detachMutation.mutateAsync({
      sitrepId,
      fileId,
    });
  };

  const handleViewFile = async (fileUrl: string, fileName: string) => {
    try {
      const url = await getSignedUrl(fileUrl);
      setFileViewUrl(url);
      setCurrentFileName(fileName);
      setShowFileDialog(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to view file');
    }
  };

  return (
    <div className="reports-file-attachments">
      {/* Attached Files Display */}
      {workspaceId && loadingAttached ? (
        <Loader />
      ) : attachedFiles.length > 0 ? (
        <div className="mb-3">
          <div className="d-flex align-items-center mb-2 gap-2">
            <SharedIcon iconName="attach_file" size="sm" />
            <span className="fw-medium small">
              Attached Files ({attachedFiles.length})
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {attachedFiles.map((file) => (
              <Badge
                key={file.id}
                variant="secondary"
                className="tw:h-auto tw:py-1"
              >
                <button
                  type="button"
                  className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit"
                  onClick={() =>
                    void handleViewFile(file.fileUrl, file.file_name)
                  }
                >
                  {file.file_name}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit"
                    aria-label={`Remove ${file.file_name}`}
                    onClick={() => void handleDetachFile(file.id)}
                  >
                    <SharedIcon iconName="close" size="sm" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      {/* Add Files Selector, opened via the report-level overflow menu */}
      {!readOnly && isExpanded && (
        <div className="mt-2">
          {/* Use exact same wrapper as AI Chat DataSources.js line 801 */}
          <div className="rounded border">
            <ChatFileSelector
              workspaceId={workspaceId!}
              selectedFileIds={selectedFileIds}
              onFileSelectionChange={setSelectedFileIds}
              selectionText="selected"
            />
          </div>

          {/* Action buttons below the selector */}
          <div className="d-flex justify-content-end mt-2 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onExpandedChange(false);
                setSelectedFileIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void handleAddFiles()}
              disabled={
                selectedFileIds.length === 0 || attachMutation.isPending
              }
            >
              {attachMutation.isPending ? 'Attaching...' : 'Attach Files'}
            </Button>
          </div>
        </div>
      )}

      {/* File View Dialog */}
      {showFileDialog && fileViewUrl && (
        <StylishViewFileDialog
          show={showFileDialog}
          onClose={() => setShowFileDialog(false)}
          fileUrl={fileViewUrl}
          fileName={currentFileName}
        />
      )}
    </div>
  );
};

export default ReportsFileAttachments;
